import { datadogRum } from '@datadog/browser-rum';
import { FC, ReactNode, createContext, useEffect, useState } from 'react';

import getUser from '@/shared/api/auth/getUser';
import login from '@/shared/api/auth/login';
import Response from '@/shared/types/response';
import User from '@/shared/types/user';

import getCookie from '../../utils/getCookie';
import removeCookie from '../../utils/removeCookie';
import setCookie from '../../utils/setCookie';

type AuthContextType = {
  isLoading: boolean;
  user: User | null;
  login: ({
    accountId,
    email,
    password,
  }: {
    accountId: string;
    email: string;
    password: string;
  }) => Promise<Response<User>>;
  logout: () => void;
  setUser?: (user: User) => void;
};

export const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  user: null,
  login: () => {
    return Promise.resolve({} as Response<User>);
  },
  logout: () => {},
});

type AuthProviderProps = {
  children: ReactNode;
};

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const handleLogin = async ({
    accountId,
    email,
    password,
  }: {
    accountId: string;
    email: string;
    password: string;
  }) => {
    try {
      const loginResponse = await login(accountId, email, password);

      if (loginResponse.success && loginResponse.auth_token) {
        removeCookie('auth_token');
        setCookie('auth_token', loginResponse.auth_token);

        const userResponse = await getUser(loginResponse.auth_token);

        if (userResponse.success && userResponse.data.length > 0) {
          setUser(userResponse.data[0]);
        }
      }
      setIsLoading(false);
      return loginResponse;
    } catch (error) {
      setIsLoading(false);
      return error as Response<User>;
    }
  };

  const handleLogout = () => {
    removeCookie('auth_token');
    setIsLoading(true);
    setUser(null);
  };

  useEffect(() => {
    const token = getCookie('auth_token');

    if (!token) {
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        const userData = await getUser(token);
        const userEntity = userData.data[0];

        datadogRum.setUser({
          id: userEntity.in_account_id,
          email: userEntity.email_address,
          name: `${userEntity.first_name} ${userEntity.last_name}`,
        });

        setUser(userEntity);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    })();
  }, []);

  const value: AuthContextType = {
    isLoading,
    user,
    login: handleLogin,
    logout: handleLogout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
